import { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { MotionViewport, varFade } from 'src/components/animate';
import { m, useScroll } from 'framer-motion';
import Iconify from 'src/components/iconify';
import ScrollProgress from 'src/components/scroll-progress';
import { bgGradient } from 'src/theme/css';
import RequestDemo from 'src/components/request-demo';
import { getFeatures, getFeatureDetails } from 'src/utils/features-utils';

export default function FeaturesView() {
  const theme = useTheme();
  const { scrollYProgress } = useScroll();
  const [activeTab, setActiveTab] = useState(0);
  const [expandedFeature, setExpandedFeature] = useState(null);

  // Get features data using theme
  const features = getFeatures(theme);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDemoRequest = async (formData) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Demo request submitted:', formData);
  };
  return (
    <>
      <ScrollProgress scrollYProgress={scrollYProgress} />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          pt: { xs: 10, md: 15 },
          pb: { xs: 10, md: 15 },
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
                theme.palette.secondary.main,
                0.08
              )} 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, ${alpha(
                theme.palette.info.main,
                0.05
              )} 0%, transparent 50%)
            `,
            zIndex: 0,
          },
        }}
      >
        <Container
          maxWidth="lg"
          component={MotionViewport}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <m.div variants={varFade().inLeft}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 2,
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    Powerful Features
                    <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                      for Modern Business
                    </Box>
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'text.primary',
                      mb: 3,
                      lineHeight: 1.6,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '1.125rem', md: '1.5rem' },
                    }}
                  >
                    Discover how Cartify&apos;s comprehensive feature set transforms your restaurant
                    operations with cutting-edge technology and intuitive design.
                  </Typography>
                  <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <RequestDemo
                      buttonText="Book Your Demo Now"
                      buttonVariant="contained"
                      buttonSize="large"
                      onDemoRequest={handleDemoRequest}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: theme.customShadows.primary,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.customShadows.primary,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </m.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <m.div variants={varFade().inRight}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    mt: { xs: 4, md: 0 },
                  }}
                >
                  {/* App Screenshot - No Frame */}
                  <Box
                    component="img"
                    src="/assets/images/phone-1.png"
                    alt="Cartify App Screenshot"
                    sx={{
                      width: { xs: 240, md: 280 },
                      height: { xs: 480, md: 560 },
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: 2,
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    }}
                  />
                </Box>
              </m.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Overview Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: '700',
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Everything You Need to Succeed
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                From real-time order management to advanced analytics, Cartify provides the tools
                you need to streamline operations and grow your business.
              </Typography>
            </Box>
          </m.div>

          {/* Features Grid */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={feature.id}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 2.5,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(5deg)',
                        },
                      },
                    }}
                    onClick={() => setActiveTab(index)}
                  >
                    <CardContent>
                      <Box
                        className="feature-icon"
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: alpha(feature.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        <Iconify
                          icon={feature.icon}
                          width={30}
                          height={30}
                          sx={{ color: feature.color }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 1.5,
                          fontSize: '1rem',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.5,
                          mb: 2,
                          fontSize: '0.875rem',
                        }}
                      >
                        {feature.description}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(index);
                          // Scroll to the third section
                          const thirdSection = document.getElementById('detailed-features-section');
                          if (thirdSection) {
                            thirdSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        sx={{
                          borderColor: feature.color,
                          color: feature.color,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          py: 0.5,
                          px: 1.5,
                          minWidth: 'auto',
                          '&:hover': {
                            borderColor: feature.color,
                            bgcolor: alpha(feature.color, 0.1),
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Detailed Features Section with Tabs */}
      <Box
        id="detailed-features-section"
        sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.neutral' }}
      >
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: '700',
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Dive Deep into Each Feature
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                Explore the details and benefits of each powerful feature that makes Cartify the
                ultimate restaurant management platform.
              </Typography>
            </Box>
          </m.div>

          {/* Mobile: Feature Cards - One at a time */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4 }}>
            {features.map((feature, index) => (
              <m.div key={feature.id} variants={varFade().inUp}>
                <Card
                  sx={{
                    mb: 3,
                    p: 3,
                    cursor: 'pointer',
                    border:
                      activeTab === index ? `2px solid ${feature.color}` : '2px solid transparent',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.customShadows.z16,
                    },
                  }}
                  onClick={() => setActiveTab(index)}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          bgcolor: alpha(feature.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                        }}
                      >
                        <Iconify
                          icon={feature.icon}
                          width={24}
                          height={24}
                          sx={{ color: feature.color }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          flex: 1,
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Box>

                    {activeTab === index && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            mb: 2,
                            lineHeight: 1.6,
                          }}
                        >
                          {feature.description}
                        </Typography>

                        {/* Benefits List */}
                        <Stack spacing={1.5} sx={{ mb: 2 }}>
                          {feature.benefits.map((benefit, benefitIndex) => (
                            <Box key={benefitIndex} sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: feature.color,
                                  mr: 1.5,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {benefit}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>

                        {/* Learn More Button */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedFeature(expandedFeature === feature.id ? null : feature.id);
                          }}
                          sx={{
                            borderColor: feature.color,
                            color: feature.color,
                            '&:hover': {
                              borderColor: feature.color,
                              bgcolor: alpha(feature.color, 0.1),
                            },
                          }}
                        >
                          {expandedFeature === feature.id ? 'Show Less' : 'Learn More'}
                        </Button>

                        {/* Expanded Details */}
                        {expandedFeature === feature.id && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: `1px solid ${alpha(feature.color, 0.2)}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                lineHeight: 1.6,
                              }}
                            >
                              {getFeatureDetails(feature.id)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </m.div>
            ))}
          </Box>

          {/* Desktop: Tabs Navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              borderBottom: 1,
              borderColor: 'divider',
              mb: 4,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 4,
                },
                '& .Mui-selected': {
                  color: 'primary.main',
                },
              }}
            >
              {features.map((feature) => (
                <Tab
                  key={feature.id}
                  label={feature.title}
                  icon={<Iconify icon={feature.icon} width={24} height={24} sx={{ mb: 1 }} />}
                  iconPosition="top"
                />
              ))}
            </Tabs>
          </Box>

          {/* Desktop: Tab Content */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <m.div variants={varFade().inUp}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 3,
                      }}
                    >
                      {features[activeTab].title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        mb: 4,
                        fontSize: '1.1rem',
                      }}
                    >
                      {features[activeTab].description}
                    </Typography>

                    {/* Benefits List */}
                    <Stack spacing={2}>
                      {features[activeTab].benefits.map((benefit, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: features[activeTab].color,
                              mr: 2,
                            }}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.primary',
                              fontWeight: 500,
                            }}
                          >
                            {benefit}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    {/* Learn More Button */}
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() =>
                        setExpandedFeature(
                          expandedFeature === features[activeTab].id ? null : features[activeTab].id
                        )
                      }
                      sx={{
                        mt: 3,
                        borderColor: features[activeTab].color,
                        color: features[activeTab].color,
                        '&:hover': {
                          borderColor: features[activeTab].color,
                          bgcolor: alpha(features[activeTab].color, 0.1),
                        },
                      }}
                    >
                      {expandedFeature === features[activeTab].id ? 'Show Less' : 'Learn More'}
                    </Button>

                    {/* Expanded Details */}
                    {expandedFeature === features[activeTab].id && (
                      <Box
                        sx={{
                          mt: 3,
                          pt: 3,
                          borderTop: `1px solid ${alpha(features[activeTab].color, 0.2)}`,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            fontSize: '0.95rem',
                          }}
                        >
                          {getFeatureDetails(features[activeTab].id)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -20,
                        left: -20,
                        right: 20,
                        bottom: 20,
                        background: `linear-gradient(45deg, ${alpha(
                          features[activeTab].color,
                          0.1
                        )} 0%, transparent 100%)`,
                        borderRadius: 4,
                        zIndex: 0,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={features[activeTab].image}
                      alt={features[activeTab].title}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 2,
                        position: 'relative',
                        zIndex: 1,
                        filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </m.div>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          ...bgGradient({
            color: alpha(theme.palette.primary.main, 0.1),
            imgUrl: '/assets/background/overlay_3.jpg',
          }),
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Ready to Experience These Features?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Join thousands of restaurants that have already transformed their operations with
                Cartify&apos;s powerful features.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
                <RequestDemo
                  buttonText="Book Your Demo Now"
                  buttonVariant="contained"
                  buttonSize="large"
                  onDemoRequest={handleDemoRequest}
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    boxShadow: theme.customShadows.primary,
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: theme.customShadows.primary,
                    },
                  }}
                />
              </Stack>
            </Box>
          </m.div>
        </Container>
      </Box>
    </>
  );
}
